// Special types of primitives to make things clearer
export type LocalDateTimeString = string; // yyyy-MM-ddTHH:mm OR yyyy-MM-ddTHH:mm:ss
export type UTCDateTimeString = string; // yyyy-MM-ddTHH:mm.xxxZ
export type LocalDateString = string; // yyyy-MM-dd

export type Integer = number;
export type GuidString = string;
export type UserId = GuidString;
export type WorkspaceReservationId = GuidString;
export type WorkdayId = GuidString;
export type NodeId = GuidString;

type OfficeDayDetails = {
  status: 'OfficeDay';
  buildingId: NodeId;
  buildingName: string;
  buildingIsValid: boolean;
};

export type NonOfficeDayDetails = {
  status: 'NotWorking' | 'WorkingRemotely';
};

export type WorkdayDetails = NonOfficeDayDetails | OfficeDayDetails;

export type Workday = {
  id: WorkdayId;
  ownerId: UserId;
  organizedBy: UserId;
  createdAt: UTCDateTimeString;
  lastUpdatedAt: UTCDateTimeString;

  // These are timezoneless: i.e. if it says 9:00 it means 9:00 wherever you are in the world
  localStart: LocalDateTimeString;
  localEnd: LocalDateTimeString;
} & WorkdayDetails;

export type OfficeWorkday = Extract<Workday, OfficeDayDetails>;

export type WorkdayStatus = Workday['status'];

// Confirmed requires workday,
// Pending, canceled and rejected do not
// pending counts towards capacity, but nothing else
// canceled and rejected only exist for reference, are not used for validations
export type ReservationStatus =
  | {
      workdayId: WorkdayId;
      status: 'Confirmed'; // The only state that gives you 100% right to a workspace
    }
  | {
      workdayId: WorkdayId;
      status: 'Denied'; // Async validation did not succeed
      // reason: string;
    }
  | {
      workdayId: WorkdayId | null;
      status:
        | 'InvitationPending' // Needs to be accepted by user
        | 'InvitationDeclined' // Rejected by user
        | 'Cancelled'; // Soft delete
    };

export type WorkspaceReservationStatus = WorkspaceReservation['status'];

export type GroupBookingDetails =
  | {
      invitationTo: WorkspaceReservationId;
      expiryDate: UTCDateTimeString;
      hasInvitations: false;
    }
  | {
      invitationTo: null;
      expiryDate: null;
      hasInvitations: boolean;
    };

type ReservationBase = {
  id: WorkspaceReservationId;
  owner: ReservationOwner;

  localStart: LocalDateTimeString;
  localEnd: LocalDateTimeString;

  // Will not be owner in case of Booking-on-behalf & group booking
  createdAt: UTCDateTimeString;
  createdBy: UserId;

  organizer: ReservationOwner;

  lastUpdatedAt: UTCDateTimeString;
  lastModifiedBy: UserId;

  reservationMethod: ReservationMethod;
} & GroupBookingDetails;

export type ReservationMethod = 'Explicit' | 'UserPreference' | 'UserPreferenceAlternative';

export type WorkspaceReservation = ReservationStatus &
  ReservationBase & {
    workspace: Workspace;
  };

export type DeskReservation = ReservationStatus &
  ReservationBase & {
    workspace: DeskWorkspace;
  };

export type AreaReservation = ReservationStatus &
  ReservationBase & {
    workspace: AreaWorkspace;
  };

export const isDeskReservation = (r: WorkspaceReservation): r is DeskReservation => r.workspace.nodeType === 'Desk';
export const isAreaReservation = (r: WorkspaceReservation): r is AreaReservation => r.workspace.nodeType === 'Area';

export type User = {
  id: UserId;
  mayBookOnBehalf: boolean;
};

// Nested workspaces to be used for enrichting workdays/workspace reservations
// For creation etc. nodeId should stillExist (workdays need type="building" and workspace bookings have bookable: true)
// When buildings are published, node ids that no longer exists get flipped to false
// Other changes are processed. Workdays and WorkspaceReservations with refernces to
// these workspaces will automatically get the update when they join in the workspace
export type BaseWorkspaceProps = {
  nodeId: NodeId;
  nodeName: string;
  isValid: boolean;
  buildingVersion: Integer;
  nodeCapacity: number;
  bookable: boolean;
};

export type BuildingProps = {
  buildingId: NodeId;
  buildingName: string;
};

export type FloorProps = {
  floorId: NodeId;
  floorLevel: Integer;
  floorName: string;
};

export type AreaProps = {
  areaId: NodeId;
  areaName: string;
};

export type DeskProps = {
  // Note: do we need desk island?
  deskIslandId: NodeId;
  deskIslandName: string;
  deskId: NodeId;
  deskName: string;
};

export type BuildingWorkspace = {nodeType: 'Building'} & BaseWorkspaceProps & BuildingProps;
export type FloorWorkspace = {nodeType: 'Floor'} & BaseWorkspaceProps & BuildingProps & FloorProps;
export type AreaWorkspace = {nodeType: 'Area'} & BaseWorkspaceProps & BuildingProps & FloorProps & AreaProps;
export type DeskWorkspace = {nodeType: 'Desk'} & BaseWorkspaceProps &
  BuildingProps &
  FloorProps &
  AreaProps &
  DeskProps;

export type Workspace = BuildingWorkspace | FloorWorkspace | AreaWorkspace | DeskWorkspace;

// PUT/POST dtos
export type WriteableWorkdayDetails = NonOfficeDayDetails | Omit<OfficeDayDetails, 'buildingName' | 'buildingIsValid'>;

export type CreateWorkdayDto = {
  // id: picked by the server
  // ownerId: added by the server from token

  localStart: LocalDateTimeString;
  localEnd: LocalDateTimeString;
} & WriteableWorkdayDetails;

export type UpdateWorkdayDto = CreateWorkdayDto & {id: WorkdayId};

export type CreateWorkspaceReservationDto = {
  localStart: LocalDateTimeString;
  localEnd: LocalDateTimeString;
  workdayId: WorkdayId;

  // When node id is not specified, the server will attempt to find a suitable workspace
  nodeId?: NodeId;
  invitations?: Array<UpsertInviteeDto>;
};

export type UpsertInviteeDto = {
  ownerId: UserId;
  nodeId: NodeId;
};

export type ReservationOwner = {
  id: string;
  name: string;
  initials: string;
  email: string;
  imageHash: string | null;
};

export type WorkspaceReservationWithOwner = WorkspaceReservation & {owner: ReservationOwner};

export type PatchWorkspaceReservationDto = {id: WorkspaceReservationId} & (
  | // For rescheduling or relocating
  {
      localStart?: LocalDateTimeString;
      localEnd?: LocalDateTimeString;
      nodeId?: NodeId;
      invitations?: Array<UpsertInviteeDto>;
    }
  // For canceling a reservation
  | {
      status: 'Cancelled';
    }
  // For accepting an invite
  | {
      status: 'Confirmed';
      workdayId: WorkdayId;
    }
  // For declining an invite
  | {
      status: 'InvitationDeclined';
    }
);

export type ReservationCount = {
  nodeId: string;
  floorId?: string;
  areaId?: string;
  deskId?: string;
  booked: Integer;
};

export type GetReservationCountQuery = {
  nodeId: string;
  startDate: LocalDateTimeString;
  endDate: LocalDateTimeString;
};
